import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { resolveAccountBookingSettings } from '@/lib/bookingSettings'
import { scrollToAndFocusField, type SettingsSaveError } from '@/lib/settingsSaveError'

// EP-53: 8 feature toggles (cmms_enabled, accounting_enabled, late_fees_enabled,
// announcements_enabled, debt_collection_enabled, lpr_enabled,
// lead_generation_enabled, microsite_enabled) were removed from this form.
// They are admin-only — set via admin-cli AccountFormPage. The READ types in
// useAccountSettings.ts + types/index.ts are intentionally kept so Sidebar,
// PropertyPricingFields, and other dual-gate consumers still type-check.
export interface AccountFormValues {
  name: string
  language: string
  timezone: string
  date_format: string
  calendar: string
  currency: string
  invoice_prefix: string
  liff_id: string
  tax_id: string
  branch_number: string
  tax_address: string
  vat_enabled: boolean
  vat_rate: number
  // EP-111 T111-6 — per-property condominium type module toggle (single-gate,
  // owner-settable; no Unleash flag). Read downstream as
  // `accountSettings.settings.condominium_enabled === true`.
  condominium_enabled: boolean
  // EP-136 (T136-026) — account-wide fallback for the booking-flow settings.
  // Unlike the property-level Booking block these three are NOT tri-state:
  // there is no settings layer above the account, so the form always carries
  // a concrete value (the resolved default when the account has none set),
  // same as vat_enabled/condominium_enabled above. booking_fee_default is the
  // one genuinely optional field — it may stay blank forever (an unset
  // account default just means the operator types the amount at booking
  // time); RHF represents "blank" as NaN via valueAsNumber, mirroring
  // deposit_value's convention elsewhere in this codebase.
  booking_fee_mode: 'deposit' | 'revenue'
  booking_fee_default: number
  booking_fee_forfeit_on_cancel: boolean
  // EP-160 (section 2.7, S1): the data controller the tenants' privacy notice
  // names. Blank means "use the default" (account name, owner email), which
  // the page shows as the placeholder rather than as a value.
  controller_name: string
  controller_contact: string
}

export function useAccountForm() {
  const { data: account, isLoading } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // EP-3a: gating the i18n side-effect — use the live i18n instance from
  // useTranslation() (matches useLanguageSync.ts) instead of importing the
  // `@/i18n` init module, and only flip the language when the user actually
  // changed it. The previous code called `i18n.changeLanguage(...)` on every
  // account-settings save (e.g. toggling `condominium_enabled` without
  // touching language), which re-emitted `languageChanged` and forced i18next
  // to re-render every consumer — perceived as a full UI reload with labels
  // visibly flipping mid-transition.
  const { i18n } = useTranslation()
  // PAY-1 (T149-083): set on a failed save (validation stop or server
  // failure) so the caller can render the persistent SettingsSaveAlert;
  // cleared on the next save attempt (success or otherwise).
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  const form = useForm<AccountFormValues>({
    defaultValues: {
      name: '',
      language: 'th',
      timezone: 'Asia/Bangkok',
      date_format: 'DD/MM/YYYY',
      calendar: 'gregorian',
      currency: 'THB',
      invoice_prefix: '',
      liff_id: '',
      tax_id: '',
      branch_number: '',
      tax_address: '',
      vat_enabled: false,
      vat_rate: 7,
      condominium_enabled: false,
      booking_fee_mode: 'deposit',
      booking_fee_default: NaN,
      booking_fee_forfeit_on_cancel: true,
      controller_name: '',
      controller_contact: '',
    },
  })

  useEffect(() => {
    if (account) {
      const resolvedBooking = resolveAccountBookingSettings(account.settings.booking_settings)
      form.reset({
        name: account.name,
        language: account.settings.language ?? 'th',
        timezone: account.settings.timezone ?? 'Asia/Bangkok',
        date_format: account.settings.date_format ?? 'DD/MM/YYYY',
        calendar: account.settings.calendar ?? 'gregorian',
        currency: account.settings.currency ?? 'THB',
        invoice_prefix: account.settings.invoice_prefix ?? '',
        liff_id: account.settings.liff_id ?? '',
        tax_id: account.settings.tax_id ?? '',
        branch_number: account.settings.branch_number ?? '',
        tax_address: account.settings.tax_address ?? '',
        vat_enabled: account.settings.vat_enabled ?? false,
        vat_rate: account.settings.vat_rate ?? 7,
        condominium_enabled: account.settings.condominium_enabled ?? false,
        booking_fee_mode: resolvedBooking.mode,
        booking_fee_default: resolvedBooking.default ?? NaN,
        booking_fee_forfeit_on_cancel: resolvedBooking.forfeitOnCancel,
        // A defaulted value is the server's fallback, not something saved, so
        // the field stays empty and the page shows the default as placeholder.
        controller_name: account.controller_name_defaulted ? '' : (account.controller_name ?? ''),
        controller_contact: account.controller_contact_defaulted ? '' : (account.controller_contact ?? ''),
      })
    }
  }, [account, form])

  // PAY-1 (T149-083): a bare form.handleSubmit(onValid) resolves silently on
  // invalid input (onValid is skipped, no error, no throw), so the caller's
  // subsequent showSuccess() used to fire regardless. The onInvalid callback
  // below is RHF's own hook for the failure case; handleSubmit(onValid,
  // onInvalid) is built fresh inside handleSave (not at the top of the hook
  // body) so its callbacks can close over a plain local variable instead of a
  // ref, which would otherwise read as render-phase ref access to the linter.
  // handleSave returns true only when the save actually happened.
  const handleSave = async (e: React.FormEvent): Promise<boolean> => {
    let succeeded = false
    const submitForm = form.handleSubmit(
      async (values) => {
        setSaveError(null)
        try {
          await updateSettings.mutateAsync({
            name: values.name,
            // Always sent, blank included: a blank clears the stored value so
            // the default applies again (Api::AccountSettingsController).
            controller_name: values.controller_name.trim(),
            controller_contact: values.controller_contact.trim(),
            settings: {
              language: values.language,
              timezone: values.timezone,
              date_format: values.date_format,
              calendar: values.calendar,
              currency: values.currency,
              invoice_prefix: values.invoice_prefix,
              liff_id: values.liff_id,
              tax_id: values.tax_id || undefined,
              branch_number: values.branch_number || undefined,
              tax_address: values.tax_address || undefined,
              vat_enabled: values.vat_enabled,
              vat_rate: values.vat_enabled ? values.vat_rate : undefined,
              condominium_enabled: values.condominium_enabled,
              // EP-136 (T136-026): booking_fee_default follows the same
              // omit-when-blank convention as tax_id/branch_number/tax_address
              // above (a blank field is dropped from the JSON payload, which the
              // BE's deep_merge then leaves untouched rather than clearing).
              booking_settings: {
                booking_fee_mode: values.booking_fee_mode,
                booking_fee_default: Number.isFinite(values.booking_fee_default)
                  ? values.booking_fee_default
                  : undefined,
                booking_fee_forfeit_on_cancel: values.booking_fee_forfeit_on_cancel,
              },
            },
          })
          // EP-3a: only flip the locale when the user actually changed it. Saves that
          // don't touch language (e.g. toggling `condominium_enabled`) used to call
          // `changeLanguage` unconditionally, which forced the entire i18n tree to
          // re-render, perceived as a full UI reload. Also persist only on a real
          // change so we don't churn localStorage on every save.
          if (values.language !== i18n.language) {
            localStorage.setItem('language', values.language)
            i18n.changeLanguage(values.language)
          }
          succeeded = true
        } catch (err) {
          setSaveError({ serverMessage: translateError(err) })
          succeeded = false
        }
      },
      (fieldErrors) => {
        const fieldKeys = Object.keys(fieldErrors)
        setSaveError({ fieldCount: fieldKeys.length })
        scrollToAndFocusField(fieldKeys[0])
        succeeded = false
      },
    )
    await submitForm(e)
    return succeeded
  }

  return {
    form,
    isLoading,
    isPending: updateSettings.isPending,
    saveError,
    handleSave,
  }
}
